import { api } from '../../lib/api';

export interface PresignedDownloadData {
  url: string;
  downloadUrl: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  expiresAt: string;
}

// In-memory presigned URL cache keyed by `${workspaceId}:${fileId}`
const urlCache = new Map<string, { data: PresignedDownloadData; fetchedAt: number }>();

// In-flight request deduplication map keyed by `${workspaceId}:${fileId}`
const inFlightRequests = new Map<string, Promise<PresignedDownloadData>>();

// Safety window before expiry: 2 minutes (120,000 ms)
const EXPIRATION_SAFETY_MARGIN_MS = 2 * 60 * 1000;

/**
 * Resolve an authorized presigned download/preview URL with deduplication and client-side caching.
 * Prevents duplicate API calls across React renders and multiple attachment components.
 */
export async function getPresignedDownloadUrl(
  workspaceId: string,
  fileId: string
): Promise<PresignedDownloadData> {
  const cacheKey = `${workspaceId}:${fileId}`;
  const now = Date.now();

  // 1. Check in-memory cache
  const cached = urlCache.get(cacheKey);
  if (cached) {
    const expiresTime = new Date(cached.data.expiresAt).getTime();
    if (!isNaN(expiresTime) && expiresTime - now > EXPIRATION_SAFETY_MARGIN_MS) {
      return cached.data;
    }
    // Expired or close to expiry -> invalidate
    urlCache.delete(cacheKey);
  }

  // 2. Check in-flight request deduplication
  const existingPromise = inFlightRequests.get(cacheKey);
  if (existingPromise) {
    return existingPromise;
  }

  // 3. Initiate new authenticated API request
  const requestPromise = (async () => {
    try {
      const raw = await api.files.getPresignedDownloadUrl(workspaceId, fileId);
      if (!raw || (!raw.downloadUrl && !raw.url)) {
        throw new Error(`Invalid presigned download URL response for file ${fileId}`);
      }

      const targetUrl = raw.downloadUrl || raw.url || '';
      const data: PresignedDownloadData = {
        url: targetUrl,
        downloadUrl: targetUrl,
        fileName: raw.fileName || '',
        contentType: raw.contentType || '',
        fileSize: raw.fileSize || 0,
        expiresAt: raw.expiresAt || new Date(Date.now() + 15 * 60 * 1000).toISOString(),
      };

      urlCache.set(cacheKey, { data, fetchedAt: Date.now() });
      return data;
    } catch (err) {
      urlCache.delete(cacheKey);
      throw err;
    } finally {
      inFlightRequests.delete(cacheKey);
    }
  })();

  inFlightRequests.set(cacheKey, requestPromise);
  return requestPromise;
}

/**
 * Invalidate cached presigned URL for a specific file (e.g. user clicks Retry)
 */
export function invalidatePresignedUrl(workspaceId: string, fileId: string): void {
  const cacheKey = `${workspaceId}:${fileId}`;
  urlCache.delete(cacheKey);
  inFlightRequests.delete(cacheKey);
}
